import { Flex, Typography, useToast } from '@iheartjane/reefer';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';

import {
  useFetchCurrentBloomMenu,
  useUpdateBloomMenu,
} from '@jane/business-admin/data-access';
import type { BloomMainMenuForm } from '@jane/business-admin/types';
import { businessPaths } from '@jane/business-admin/util';

import { ModalPageWrapper } from './components/ModalPageWrapper';
import { PremiumLinks } from './components/PremiumLinks';

import styles from './PremiumMainMenu.module.css';

export const PremiumMainMenu = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const bloomMenuQuery = useFetchCurrentBloomMenu();
  const { mutate: updateBloomMenu, isPending } = useUpdateBloomMenu(
    bloomMenuQuery.data?.id
  );

  const formMethods = useForm<BloomMainMenuForm>({
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const {
    reset,
    formState: { isValid },
    trigger,
    watch,
  } = formMethods;
  const menuItems = watch(`menuItems`);
  const topLevelMenuItems = menuItems || [];
  const hasMenuItems = topLevelMenuItems.length > 0;
  const disablePreview = !hasMenuItems || !isValid || isPending;

  useEffect(() => {
    if (bloomMenuQuery.isSuccess) {
      reset({
        menuItems: bloomMenuQuery.data?.theme.bloom?.main_menu || [],
      });
    }
  }, [
    bloomMenuQuery.data?.theme.bloom?.main_menu,
    bloomMenuQuery.isSuccess,
    reset,
  ]);

  useEffect(() => {
    trigger();
  }, [JSON.stringify(menuItems), trigger]);

  const publish = useCallback(
    (data: BloomMainMenuForm) => {
      updateBloomMenu(
        {
          theme: {
            ...bloomMenuQuery.data?.theme?.bloom,
            main_menu: data.menuItems,
          },
        },
        {
          onError: () => {
            toast.add({
              label: 'An error occurred.',
              variant: 'error',
            });
          },
          onSuccess: () => {
            toast.add({
              label: 'Your settings have been saved!',
              variant: 'success',
            });
            reset({ menuItems: data.menuItems });
            navigate(businessPaths.premium());
          },
        }
      );
    },
    [bloomMenuQuery.data?.theme?.bloom, toast, reset]
  );

  const onPreview = useCallback(() => {
    updateBloomMenu(
      {
        theme_draft: {
          ...bloomMenuQuery.data?.theme?.bloom,
          main_menu: menuItems,
        },
      },
      {
        onError: () => {
          toast.add({
            label: 'An error occurred.',
            variant: 'error',
          });
        },
        onSuccess: () => {
          if (!bloomMenuQuery.data?.menu_url) {
            return;
          }
          const previewUrl = new URL(bloomMenuQuery.data.menu_url);
          previewUrl.searchParams.set('draft', 'true');
          const newWindow = window.open(
            previewUrl,
            '_blank',
            'noopener,noreferrer'
          );
          if (newWindow) {
            newWindow.opener = null;
          }
        },
      }
    );
  }, [bloomMenuQuery, menuItems, updateBloomMenu, toast]);

  return (
    <ModalPageWrapper
      formMethods={formMethods}
      isLoading={isPending}
      onSubmit={publish}
      preview={{ onClick: onPreview, disable: disablePreview }}
      title="Main menu"
    >
      <Flex px={40} pt={24} pb={48} gap={24} flexDirection="column">
        <Typography variant="body">
          Set up the items that will appear in the main menu. Drag each menu
          option into the order you prefer.
        </Typography>

        <Flex
          className={styles['contentContainer']}
          background="grays-white"
          borderRadius="sm"
          flexDirection="column"
          gap={hasMenuItems ? 4 : 8}
        >
          {!hasMenuItems && (
            <Flex justifyContent="center">
              <Typography>Your menu is empty</Typography>
            </Flex>
          )}
          <Flex flexDirection="column" gap={hasMenuItems ? 8 : 0}>
            <PremiumLinks
              addButtonLabel="Add menu item"
              formMethods={formMethods}
              controlName="menuItems"
            />
          </Flex>
        </Flex>
      </Flex>
    </ModalPageWrapper>
  );
};
